import { FrameData } from '../../types';
import { BeatPulse } from '../../utils/audioUtils';
import { hsl } from '../../utils/colorUtils';
import { LayerRenderer } from './LayerRenderer';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  life: number;
  maxLife: number;
  hue: number;
}

export class ParticlesRenderer implements LayerRenderer {
  private particles: Particle[] = [];
  private beatPulse = new BeatPulse(0.9);
  private width = 0;
  private height = 0;
  private currentCount = 0;
  private currentSpeed = 0.3;
  private currentHue = 260;

  init(canvas: HTMLCanvasElement): void {
    this.width = canvas.width;
    this.height = canvas.height;
  }

  private createParticle(burst = false): Particle {
    const cx = this.width / 2;
    const cy = this.height / 2;

    if (burst) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 1.5 + Math.random() * 3;
      return {
        x: cx + (Math.random() - 0.5) * 40,
        y: cy + (Math.random() - 0.5) * 40,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: 1.5 + Math.random() * 2.5,
        life: 1,
        maxLife: 1500 + Math.random() * 2000,
        hue: this.currentHue + Math.random() * 60 - 30,
      };
    }

    return {
      x: Math.random() * (this.width || 800),
      y: Math.random() * (this.height || 600),
      vx: (Math.random() - 0.5) * this.currentSpeed,
      vy: (Math.random() - 0.5) * this.currentSpeed,
      size: 1 + Math.random() * 2,
      life: Math.random(),
      maxLife: 3000 + Math.random() * 5000,
      hue: this.currentHue + Math.random() * 60 - 30,
    };
  }

  private ensureParticleCount(count: number): void {
    if (count === this.currentCount && this.particles.length === count) return;
    this.currentCount = count;
    // Add or remove particles to match count
    while (this.particles.length < count) {
      this.particles.push(this.createParticle());
    }
    if (this.particles.length > count) {
      this.particles.length = count;
    }
  }

  render(
    ctx: CanvasRenderingContext2D,
    frame: FrameData,
    settings: Record<string, number>,
    width: number,
    height: number,
  ): void {
    const { bassEnergy, trebleEnergy, isBeat, beatIntensity, deltaTime } = frame;
    const particleCount = Math.round(settings.particleCount ?? 200);
    this.currentSpeed = settings.baseSpeed ?? 0.3;
    this.currentHue = settings.colorHue ?? 260;
    this.width = width;
    this.height = height;

    this.ensureParticleCount(particleCount);
    this.beatPulse.update(isBeat, beatIntensity);

    const cx = width / 2;
    const cy = height / 2;

    // On beat: push particles outward
    if (isBeat) {
      for (const p of this.particles) {
        const dx = p.x - cx;
        const dy = p.y - cy;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const pushStrength = beatIntensity * 2;
        p.vx += (dx / dist) * pushStrength;
        p.vy += (dy / dist) * pushStrength;
      }
    }

    const dt = deltaTime / 16.67;

    for (const p of this.particles) {
      p.life -= deltaTime / p.maxLife;

      if (p.life <= 0) {
        Object.assign(p, this.createParticle());
        continue;
      }

      const dx = p.x - cx;
      const dy = p.y - cy;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;
      p.vx += (dx / dist) * bassEnergy * 0.15 * dt;
      p.vy += (dy / dist) * bassEnergy * 0.15 * dt;

      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= 0.98;
      p.vy *= 0.98;

      // Wrap edges
      if (p.x < -10) p.x = width + 10;
      if (p.x > width + 10) p.x = -10;
      if (p.y < -10) p.y = height + 10;
      if (p.y > height + 10) p.y = -10;

      const hue = p.hue + trebleEnergy * 80;
      const alpha = p.life * 0.8;
      const size = p.size + this.beatPulse.value * 1.5;

      ctx.beginPath();
      ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
      ctx.fillStyle = hsl(hue, 70, 60, alpha);
      ctx.fill();
    }
  }

  resize(width: number, height: number): void {
    this.width = width;
    this.height = height;
  }

  destroy(): void {
    this.particles = [];
  }
}
